import { CheckCircle, Award, Shield, Truck } from "lucide-react";
import { motion } from "framer-motion";

const features = [
  {
    icon: <Award className="w-12 h-12 text-[#C6A76D]" />,
    title: "Calidad Premium",
    description: "Materiales de la más alta calidad para garantizar durabilidad y elegancia en cada detalle.",
    direction: "left" // Viene desde la izquierda
  },
  {
    icon: <CheckCircle className="w-12 h-12 text-[#C6A76D]" />,
    title: "Diseño Exclusivo",
    description: "Variedad de diseños modernos y clásicos que se adaptan a cualquier estilo de decoración.",
    direction: "right" // Viene desde la derecha
  },
  {
    icon: <Shield className="w-12 h-12 text-[#C6A76D]" />,
    title: "Resistencia",
    description: "Fabricados para resistir el paso del tiempo, manteniendo su belleza y funcionalidad.",
    direction: "left" // Viene desde la izquierda
  },
  {
    icon: <Truck className="w-12 h-12 text-[#C6A76D]" />,
    title: "Entrega Garantizada",
    description: "Servicio de entrega confiable y seguro en toda la república mexicana.",
    direction: "right" // Viene desde la derecha
  }
];

const FeatureCard = ({ feature, index }: { feature: typeof features[0], index: number }) => {
  // Determinar la dirección de entrada
  const getInitialX = () => {
    if (feature.direction === "left") return -100;
    if (feature.direction === "right") return 100;
    return 0;
  };

  return (
    <motion.div
      initial={{ 
        opacity: 0, 
        x: getInitialX(),
        y: 30,
        scale: 0.9
      }}
      whileInView={{ 
        opacity: 1, 
        x: 0,
        y: 0,
        scale: 1,
        transition: {
          duration: 0.8,
          delay: index * 0.15,
          ease: [0.25, 0.46, 0.45, 0.94] // Curva de animación suave
        }
      }}
      viewport={{ once: true, amount: 0.3 }}
      whileHover={{ 
        y: -12,
        scale: 1.05,
        transition: { duration: 0.3 }
      }}
      className="group relative bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-[#C6A76D]/20 overflow-hidden"
    >
      {/* Efecto de brillo al hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#C6A76D]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      
      {/* Borde animado */}
      <div className="absolute inset-0 border-2 border-[#C6A76D] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      
      <div className="relative z-10">
        {/* Ícono con animación */}
        <motion.div 
          className="mb-6 inline-block"
          whileHover={{ 
            rotate: [0, -10, 10, -10, 0],
            scale: 1.1,
            transition: { duration: 0.5 }
          }}
        >
          <div className="p-4 bg-gradient-to-br from-[#C6A76D]/10 to-[#e2c98a]/10 rounded-xl group-hover:from-[#C6A76D]/20 group-hover:to-[#e2c98a]/20 transition-all duration-300">
            {feature.icon}
          </div>
        </motion.div>
        
        <h3 className="text-xl font-bold text-[#2A2520] mb-3 group-hover:text-[#C6A76D] transition-colors duration-300">
          {feature.title}
        </h3>
        <p className="text-gray-600 leading-relaxed">
          {feature.description}
        </p>
      </div>
    </motion.div>
  );
};

const FeaturesSection = () => {
  return (
    <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-b from-[#F7F4F0] via-white to-[#F7F4F0]">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden opacity-5">
        <div className="absolute -top-1/2 -left-1/4 w-[800px] h-[800px] bg-gradient-to-br from-[#C6A76D] to-transparent rounded-full filter blur-3xl animate-float"></div>
        <div className="absolute -bottom-1/2 -right-1/4 w-[800px] h-[800px] bg-gradient-to-tl from-[#C6A76D] to-transparent rounded-full filter blur-3xl animate-float animation-delay-2000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header con animación */}
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2 
            className="text-4xl md:text-5xl font-bold text-[#2A2520] mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Por qué elegir nuestros lambrines
          </motion.h2>
          
          <motion.div 
            className="w-24 h-1 bg-gradient-to-r from-transparent via-[#C6A76D] to-transparent mx-auto my-6"
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: 96, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          ></motion.div>
          
          <motion.p 
            className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            Calidad, diseño y durabilidad en cada uno de nuestros productos
          </motion.p>
        </motion.div>

        {/* Grid de características */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <FeatureCard key={index} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
